<script setup lang="ts">
import WrIcon from "~/components/global/WrIcon.vue";

const colorMode = useColorMode();
const isLight = computed(() => colorMode.value !== "dark");

function accentGradient(index: number): string {
  if (index % 2 === 0) return 'linear-gradient(90deg, #12DBFF, #BA55FE)';
  return 'linear-gradient(90deg, #BA55FE, #12DBFF)';
}

function arrowGradientId(index: number): string {
  return 'arrow-grad-' + index;
}

interface PipelineStep {
  number: string;
  title: string;
  description: string;
  svgIcon: string;
}

defineProps<{
  heading: string;
  steps: PipelineStep[];
  ariaLabel?: string;
}>();
</script>

<template>
  <section class="mt-24 lg:mt-[100px] mb-16 lg:mb-[100px]" :aria-label="ariaLabel ?? heading">
    <!-- Heading -->
    <div class="text-center mb-10">
      <h2
        class="text-[28px] lg:text-[48px] font-semibold leading-tight font-avant"
        style="background: linear-gradient(92deg, #12DBFF -3.57%, #BA55FE 80.72%); background-clip: text; -webkit-background-clip: text; -webkit-text-fill-color: transparent;"
      >
        {{ heading }}
      </h2>
    </div>

    <!-- Desktop: Horizontal Pipeline -->
    <div class="hidden lg:block px-4 max-w-[1200px] mx-auto">
      <div class="relative">
        <!-- Background decorative gradient line -->
        <div
          class="absolute top-1/2 -translate-y-1/2 left-0 right-0 h-1 rounded-full opacity-30"
          style="background: linear-gradient(90deg, #12DBFF 0%, #BA55FE 50%, #12DBFF 100%);"
        ></div>

        <!-- Pipeline Cards Row -->
        <div class="relative flex items-stretch justify-between gap-4">
          <template v-for="(step, index) in steps" :key="index">
            <div class="relative flex-1 group">
              <!-- Card -->
              <div
                class="relative h-full p-6 flex flex-col items-center text-center rounded-[20px] border overflow-hidden"
                :style="isLight
                  ? { background: 'linear-gradient(180deg, #FFF 0%, #F9F3FF 100%)', border: '1px solid #E7CFFF', boxShadow: '0 8px 40px rgba(97, 63, 170, 0.12)' }
                  : { background: 'linear-gradient(180deg, #1B022B 0%, #12002C 100%)', border: '1px solid #3D1B60', boxShadow: '0 8px 40px rgba(161, 37, 243, 0.15)' }
                "
              >
                <!-- Top gradient accent line -->
                <div
                  class="absolute top-0 left-0 right-0 h-[3px]"
                  :style="{ background: accentGradient(index) }"
                ></div>

                <!-- Icon -->
                <div
                  class="mb-4 w-14 h-14 rounded-xl flex items-center justify-center"
                  :style="isLight
                    ? { background: 'rgba(97, 63, 170, 0.08)', border: '1px solid rgba(97, 63, 170, 0.12)' }
                    : { background: 'rgba(165, 97, 255, 0.12)', border: '1px solid rgba(165, 97, 255, 0.2)' }
                ">
                  <WrIcon
                    :name="step.svgIcon"
                    :class="isLight ? 'size-7 text-[#613FAA]' : 'size-7 text-[#CDA0FE]'"
                  />
                </div>

                <!-- Title -->
                <h3
                  class="font-semibold text-[18px] leading-normal font-avant-demi"
                  :class="isLight ? 'text-[#613FAA]' : 'text-white'"
                >
                  {{ step.title }}
                </h3>

                <!-- Description -->
                <p
                  class="mt-3 text-[14px] leading-[18px] font-avant"
                  :class="isLight ? 'text-[#333333]' : 'text-white/80'"
                >
                  {{ step.description }}
                </p>
              </div>

              <!-- Arrow Connector -->
              <div
                v-if="index < steps.length - 1"
                class="absolute top-1/2 -right-[30px] -translate-y-1/2 z-10"
              >
                <svg
                  width="40"
                  height="20"
                  viewBox="0 0 40 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <defs>
                    <linearGradient :id="arrowGradientId(index)" x1="0" y1="10" x2="40" y2="10" gradientUnits="userSpaceOnUse">
                      <stop offset="0" stop-color="#BA55FE" />
                      <stop offset="1" stop-color="#12DBFF" />
                    </linearGradient>
                  </defs>
                  <path
                    d="M0 10H32L26 4"
                    :stroke="`url(#${arrowGradientId(index)})`"
                    stroke-width="2.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M32 10L26 16"
                    :stroke="`url(#${arrowGradientId(index)})`"
                    stroke-width="2.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>

    <!-- Mobile: Vertical Pipeline -->
    <div class="lg:hidden px-4 max-w-[600px] mx-auto">
      <div class="relative">
        <!-- Background vertical line -->
        <div
          class="absolute left-[15px] top-0 bottom-0 w-[3px] rounded-full opacity-30"
          style="background: linear-gradient(180deg, #12DBFF 0%, #BA55FE 50%, #12DBFF 100%);"
        ></div>

        <template v-for="(step, index) in steps" :key="index">
          <div class="relative flex items-start gap-4">
            <!-- Dot on the line -->
            <div class="relative z-10 flex flex-col items-center">
              <div
                class="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-5"
                :style="isLight
                  ? { background: 'linear-gradient(135deg, #613FAA 0%, #9748FF 100%)' }
                  : { background: 'linear-gradient(135deg, #7031C2 0%, #A561FF 100%)' }
                "
              >
                <span class="text-[10px] font-bold text-white font-avant-demi">
                  {{ step.number }}
                </span>
              </div>
            </div>

            <!-- Step Card -->
            <div
              class="relative flex-1 p-5 rounded-2xl border overflow-hidden"
              :style="isLight
                ? { background: 'linear-gradient(180deg, #FFF 0%, #F9F3FF 100%)', border: '1px solid #E7CFFF', boxShadow: '0 8px 40px rgba(97, 63, 170, 0.12)' }
                : { background: 'linear-gradient(180deg, #1B022B 0%, #12002C 100%)', border: '1px solid #3D1B60', boxShadow: '0 8px 40px rgba(161, 37, 243, 0.15)' }
              "
            >
              <!-- Top gradient accent line -->
              <div
                class="absolute top-0 left-0 right-0 h-[3px]"
                :style="{ background: accentGradient(index), clipPath: 'inset(0 0 0 0 round 18px 18px 0 0)' }"
              ></div>

              <!-- Icon -->
              <div
                class="mb-3 w-10 h-10 rounded-lg flex items-center justify-center"
                :style="isLight
                  ? { background: 'rgba(97, 63, 170, 0.08)', border: '1px solid rgba(97, 63, 170, 0.12)' }
                  : { background: 'rgba(165, 97, 255, 0.12)', border: '1px solid rgba(165, 97, 255, 0.2)' }
                "
              >
                <WrIcon
                  :name="step.svgIcon"
                  :class="isLight ? 'size-5 text-[#613FAA]' : 'size-5 text-[#CDA0FE]'"
                />
              </div>

              <h3
                class="font-semibold text-[17px] leading-normal font-avant-demi"
                :class="isLight ? 'text-[#613FAA]' : 'text-white'"
              >
                {{ step.title }}
              </h3>

              <p
                class="mt-2 text-[14px] leading-[18px] font-avant"
                :class="isLight ? 'text-[#333333]' : 'text-white/80'"
              >
                {{ step.description }}
              </p>
            </div>
          </div>

          <!-- Spacing between cards -->
          <div v-if="index < steps.length - 1" class="h-6"></div>
        </template>
      </div>
    </div>
  </section>
</template>
